<script setup lang="ts">
import type { CarouselProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import { computed } from 'vue';

import {
    AuctionState,
    DirectSaleState,
    getApiAuctionsListingQueryKey,
    getApiDirectSalesListingQueryKey,
    type OrderByFieldDto,
    SalesListingSortBy,
    SellingMethod,
    SortOrder,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import type { PlatformSellingMethod, SaleListingItemEntity } from '~/types/sale.type';

export interface StoryblokSaleListingCarouselProps {
    blok: StoryblokSaleListingCarousel;
}

// PROPS
const { blok } = defineProps<StoryblokSaleListingCarouselProps>();

// DATA
const KIND_TO_SELLING_METHOD_MAP: Record<StoryblokSaleListingCarousel['kind'], PlatformSellingMethod> = {
    ending_soon_auctions: SellingMethod.Auction,
    trending_auctions: SellingMethod.Auction,
    trending_directSales: SellingMethod.DirectSale,
    featured_auctions: SellingMethod.Auction,
    featured_directSales: SellingMethod.DirectSale,
};
const KIND_TO_ORDER_BY_MAP: Record<StoryblokSaleListingCarousel['kind'], OrderByFieldDto[] | undefined> = {
    ending_soon_auctions: [{ field: SalesListingSortBy.expiresAt, order: SortOrder.desc }],
    trending_auctions: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
    trending_directSales: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
    featured_auctions: [],
    featured_directSales: [],
};
const sellingMethod = computed(() => KIND_TO_SELLING_METHOD_MAP[blok.kind]);
const salesCount = computed(() => (blok.salesCount ? Number(blok.salesCount) : 8));
const auctionApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [AuctionState.Running],
    itemsPerPage: salesCount.value,
}));

const { data: auctionData, suspense: suspenseAuctions } = useApiAuctionsListing(auctionApiParams, {
    query: { enabled: computed(() => sellingMethod.value === SellingMethod.Auction), queryKey: getApiAuctionsListingQueryKey(auctionApiParams) },
});
sellingMethod.value === SellingMethod.Auction && (await suspenseAuctions());

const directSalesApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [DirectSaleState.Open],
    itemsPerPage: salesCount.value,
}));
const { data: directSaleData, suspense: suspenseDirectSales } = useApiDirectSalesListing(directSalesApiParams, {
    query: {
        enabled: computed(() => sellingMethod.value === SellingMethod.DirectSale),
        queryKey: getApiDirectSalesListingQueryKey(directSalesApiParams),
    },
});
sellingMethod.value === SellingMethod.DirectSale && (await suspenseDirectSales());

const sales = computed<SaleListingItemEntity[]>(() =>
    sellingMethod.value === SellingMethod.Auction ? (auctionData.value?.auctions ?? []) : (directSaleData.value?.directSales ?? [])
);

const carouselProps = computed<Omit<CarouselProps, 'items'>>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokSaleListingCarousel>(blok), ['_uid', 'component', 'kind', 'title', 'salesCount', 'itemsPerPage'])
);
const ui = useStoryblokCarouselCarouselUi(() => blok.itemsPerPage ?? '4');
</script>

<template>
    <div v-if="sales.length" v-editable="blok" class="space-y-4">
        <h4 v-if="!!blok.title" class="text-center text-xl font-semibold">{{ blok.title }}</h4>
        <UCarousel v-slot="{ item }" v-bind="carouselProps" :items="sales" :ui="ui">
            <SlpCard :sale="item" />
        </UCarousel>
    </div>
</template>
